import React, { useState, useRef, useEffect, lazy, Suspense } from 'react';
import {
  Container,
  Box,
  Paper,
  Button,
  CircularProgress,
  Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { usePlans, useNotification, useStorage, useAuth } from '../contexts';
import { getSharedPlanFromApi } from '../utils/sharing';
import { decodeSharedRacePlan } from '../utils/storage';
import { saveElevationData } from '../utils/elevationStorage';
import LoadingOverlay from './ui/LoadingOverlay';
import HomeScreen from './ui/HomeScreen';
import Header from './ui/Header';
import RaceForm from './form/RaceForm';
import { AnonymousUserPrompt } from './auth/AnonymousUserPrompt';
import TrackPage from './track/TrackPage';

// Lazy load heavy components
const Results = lazy(() => import('./results/Results'));

const CREW_TAB_SESSION_KEY = 'raceplan_open_crew_tab';

function shouldOpenCrewTabFromHash() {
  const h = (window.location.hash || '').replace(/^#/, '').toLowerCase();
  return h === 'crew';
}

/** Paths served by the SPA (no root 404.html — unknown URLs get in-app not found). */
function isKnownAppPath(pathname) {
  const path = (pathname || '/').replace(/\/+$/, '') || '/';
  if (path === '/' || path === '/track') return true;
  return /^\/(share|api\/plans)\/[A-Za-z0-9]{8}$/.test(path);
}

// Loading fallback
const ResultsLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
    <CircularProgress />
  </Box>
);

/**
 * AppRouter Component
 * 
 * Handles the main application routing and UI logic.
 * Replaces most of the old App.js functionality.
 */
const AppRouter = () => {
  const { 
    plans, 
    currentPlan, 
    currentFormData,
    calculatePlan,
    loadPlan,
    clearCurrentPlan,
    clearEverything,
    setCurrentFormData,
    deletePlan,
    loading: plansLoading 
  } = usePlans();
  
  const { showNotification } = useNotification();
  const { updateStorage } = useStorage();
  const { user } = useAuth();
  
  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showAnonymousPrompt, setShowAnonymousPrompt] = useState(false);
  const [hasShownPromptThisSession, setHasShownPromptThisSession] = useState(false);
  const fileInputRef = useRef(null);
  const resultsContainerRef = useRef(null);
  const formContainerRef = useRef(null);

  // Check for shared plan in URL on mount
  useEffect(() => {
    checkForSharedPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount to check URL

  // Scroll to results when they appear
  useEffect(() => {
    if (currentPlan && resultsContainerRef.current) {
      setTimeout(() => {
        resultsContainerRef.current.scrollIntoView({ behavior: 'smooth' });
      }, 200);
    }
  }, [currentPlan]);

  /**
   * Check if URL contains a shared plan and load it
   */
  const checkForSharedPlan = async () => {
    const path = window.location.pathname;
    
    // Check for /share/:id or /api/plans/:id format
    if (path.match(/^\/(share|api\/plans)\/([A-Za-z0-9]{8})$/)) {
      const segments = path.split('/');
      const id = segments.pop();
      const type = segments[segments.length - 1];
      const isApiPlan = type === 'plans' || segments.includes('api');
      
      console.log('Found plan ID in URL:', id, 'type:', type);
      
      setIsLoading(true);
      
      try {
        let loadedPlan;
        
        if (isApiPlan) {
          // Cloud-stored plan
          const response = await fetch(`/api/plans/${id}`);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          
          loadedPlan = await response.json();
          loadedPlan.isCloudStored = true;
          loadedPlan.id = id;
          loadedPlan.cloudUrl = `${window.location.origin}/api/plans/${id}`;
          
          showNotification('Plan loaded from cloud storage.', 'success');
        } else {
          // Shared plan (read-only)
          loadedPlan = await getSharedPlanFromApi(id);
          loadedPlan.id = id;
          loadedPlan.isSharedPlan = true;
          loadedPlan.readOnly = true;
          
          showNotification('Shared race plan loaded! This is a read-only version.', 'info');
        }
        
        // Store elevation data if available
        if (loadedPlan.formData?.elevationData) {
          saveElevationData(loadedPlan.formData.elevationData, id);
        }
        
        if (shouldOpenCrewTabFromHash()) {
          try {
            sessionStorage.setItem(CREW_TAB_SESSION_KEY, '1');
          } catch {
            /* ignore */
          }
        }
        // Clean URL
        window.history.replaceState(null, '', '/');
        
        // Load the plan
        loadPlan(loadedPlan);
      } catch (error) {
        console.error('Error loading plan:', error);
        showNotification('Failed to load plan. The link may be invalid or expired.', 'error');
      } finally {
        setIsLoading(false);
      }
    }
    
    // Check for legacy hash-based sharing
    const hash = window.location.hash;
    if (hash && hash.startsWith('#/share/')) {
      const encodedPlan = hash.replace('#/share/', '');
      
      setIsLoading(true);
      
      try {
        const sharedPlan = decodeSharedRacePlan(encodedPlan);
        if (sharedPlan) {
          window.history.replaceState(null, '', window.location.pathname);
          sharedPlan.readOnly = true;
          
          if (sharedPlan.formData?.elevationData) {
            saveElevationData(sharedPlan.formData.elevationData, sharedPlan.id);
          }
          
          loadPlan(sharedPlan);
          showNotification('Shared race plan loaded!', 'info');
        }
      } catch (error) {
        console.error('Error loading legacy shared plan:', error);
        showNotification('Failed to load shared plan.', 'error');
      } finally {
        setIsLoading(false);
      }
    }
  };

  /**
   * Handle form submission
   */
  const handleFormSubmit = (formData) => {
    // Convert time values to decimal hours
    if (formData.cutoffTimeHours || formData.cutoffTimeMinutes) {
      const hours = parseInt(formData.cutoffTimeHours || 0, 10);
      const minutes = parseInt(formData.cutoffTimeMinutes || 0, 10);
      formData.cutoffTime = Number(hours) + (Number(minutes) / 60);
    }

    if (formData.goalTimeHours || formData.goalTimeMinutes) {
      const hours = parseInt(formData.goalTimeHours || 0, 10);
      const minutes = parseInt(formData.goalTimeMinutes || 0, 10);
      formData.goalTime = Number(hours) + (Number(minutes) / 60);
    }

   
    // Calculate the plan
    const results = calculatePlan(formData);

    // Store elevation tagged to this plan so cache cannot leak to other plans
    if (formData.elevationData && formData.elevationData.length > 0 && results?.id) {
      saveElevationData(formData.elevationData, results.id);
    }

    if (results) {
      setShowForm(false);
      showNotification('Race plan calculated successfully!', 'success');
      
      // Show anonymous user prompt if not logged in and haven't shown it this session
      if (!user && !hasShownPromptThisSession) {
        setTimeout(() => {
          setShowAnonymousPrompt(true);
          setHasShownPromptThisSession(true);
        }, 1500); // Delay slightly so success notification shows first
      }
    } else {
      showNotification('Error calculating race plan. Please check your inputs.', 'error');
    }
  };

  /**
   * Handle file import
   */
  const handleImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedData = JSON.parse(e.target.result);
        let importedResults = importedData.results || importedData;
        
        // Clean up any read-only flags
        importedResults = {
          ...importedResults,
          isSharedPlan: false,
          readOnly: false,
          id: `plan_${Date.now()}`
        };
        
        delete importedResults.cloudUrl;
        delete importedResults.isCloudStored;
        
        loadPlan(importedResults);
        setShowForm(false);
        showNotification('Plan imported successfully!', 'success');
      } catch (error) {
        console.error('Import error:', error);
        showNotification('Error importing plan. Invalid file format.', 'error');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  /**
   * Handle starting a new plan
   */
  const handleStartNewPlan = () => {
    // Use clearEverything if available, otherwise clearCurrentPlan
    if (clearEverything) {
      clearEverything();
    } else {
      clearCurrentPlan();
      setCurrentFormData(null);
    }
    setShowForm(true);
  };

  /**
   * Handle loading an existing plan
   */
  const handleLoadPlan = (plan) => {
    // Deep clone to avoid reference issues
    const clonedPlan = JSON.parse(JSON.stringify(plan));
    clonedPlan._skipAutoSave = true;
    clonedPlan.readOnly = false;
    clonedPlan.isSharedPlan = false;
    
    loadPlan(clonedPlan);
    setShowForm(false);
    updateStorage();
    
    setTimeout(() => {
      resultsContainerRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  /**
   * Handle editing results (from results page)
   */
  const handleEditResults = () => {
    if (!currentPlan) return;
    
    // Create editable copy
    const editablePlan = JSON.parse(JSON.stringify(currentPlan));
    
    if (currentPlan.isSharedPlan || currentPlan.readOnly) {
      editablePlan.id = `plan_${Date.now()}`;
      delete editablePlan.readOnly;
      delete editablePlan.isSharedPlan;
      delete editablePlan.cloudUrl;
      delete editablePlan.isCloudStored;
      
      showNotification('You are now editing a copy of the shared plan.', 'info');
    }
    
    setCurrentFormData(editablePlan.formData);
    setShowForm(true);
    clearCurrentPlan();
    
    // Scroll to top
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      formContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  };

  /**
   * Handle editing a plan (from home screen card)
   */
  const handleEditPlan = (plan) => {
    console.log('AppRouter: Editing plan:', plan.formData?.raceName);
    
    // Deep clone to avoid reference issues
    const editablePlan = JSON.parse(JSON.stringify(plan));
    
    // Clean up any read-only flags
    delete editablePlan.readOnly;
    delete editablePlan.isSharedPlan;
    
    // Clear the current plan results (but keep formData for editing)
    clearCurrentPlan();
    
    // Set the form data (this will populate the form with existing data)
    setCurrentFormData(editablePlan.formData);
    
    // Show the form
    setShowForm(true);
    
    // Scroll to top
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      formContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  /**
   * Handle deleting a plan
   */
  const handleDeletePlan = async (planId) => {
    try {
      await deletePlan(planId);
      updateStorage();
      showNotification('Race plan deleted', 'success');
    } catch (error) {
      showNotification('Error deleting plan', 'error');
    }
  };

  const pathname =
    typeof window !== 'undefined'
      ? window.location.pathname.replace(/\/+$/, '') || '/'
      : '/';

  if (pathname === '/track') {
    return (
      <>
        {isLoading && <LoadingOverlay />}
        <TrackPage />
      </>
    );
  }

  if (!isKnownAppPath(pathname)) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 3,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            maxWidth: 480,
            textAlign: 'center',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Typography variant="h5" gutterBottom>
            Page not found
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            This address does not match a RacePlan page. Check the link or return home.
          </Typography>
          <Button variant="contained" href="/">
            Go to home
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <>
      {isLoading && <LoadingOverlay />}
      
      <Box sx={{ 
        flexGrow: 1, 
        minHeight: '100vh', 
        bgcolor: 'background.default',
        color: 'text.primary'
      }}>
        {/* Header with Plans Dropdown */}
        <Header 
          onImportClick={() => fileInputRef.current?.click()}
          onTitleClick={() => {
            clearCurrentPlan();
            setShowForm(false);
          }}
          fileInputRef={fileInputRef}
          plans={plans}
          onNewPlan={handleStartNewPlan}
          onManageStorage={() => {
            // Navigate to home screen to see storage info
            clearCurrentPlan();
            setShowForm(false);
          }}
        />
        
        {/* Edit Plan Button (shown when viewing a plan) */}
        {currentPlan && !currentPlan.readOnly && !currentPlan.isSharedPlan && (
          <Box sx={{ bgcolor: 'background.default', py: 1, px: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Button
              variant="outlined" 
              startIcon={<EditIcon />}
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                handleEditResults();
              }}
              size="small"
            >
              Edit Plan
            </Button>
          </Box>
        )}
        
        {/* Hidden file input for imports */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleImport}
          accept=".json"
          style={{ display: 'none' }}
        />
        
        {/* Main Content */}
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          {/* Home Screen */}
          {!showForm && !currentPlan && (
            <Box
              sx={{
                animation: 'fadeIn 0.3s ease-in',
                '@keyframes fadeIn': {
                  from: { opacity: 0 },
                  to: { opacity: 1 }
                }
              }}
            >
              <HomeScreen 
                plans={plans}
                onStartNewPlan={handleStartNewPlan}
                onLoadPlan={handleLoadPlan}
                onEditPlan={handleEditPlan}
                onDeletePlan={handleDeletePlan}
              />
            </Box>
          )}
          
          {/* Form */}
          {showForm && (
            <Paper 
              ref={formContainerRef}
              elevation={0} 
              sx={{ 
                p: 3, 
                mb: 4, 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                animation: 'fadeIn 0.3s ease-in',
                '@keyframes fadeIn': {
                  from: { opacity: 0 },
                  to: { opacity: 1 }
                }
              }}
            >
              <RaceForm 
                onSubmit={handleFormSubmit} 
                initialData={currentFormData} 
                isEditing={!!currentFormData?.raceName}
                raceName={currentFormData?.raceName || ''}
              />
            </Paper>
          )}
          
          {/* Results */}
          {currentPlan && (
            <Paper 
              ref={resultsContainerRef} 
              elevation={0} 
              sx={{ 
                p: 3, 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                animation: 'fadeIn 0.3s ease-in',
                '@keyframes fadeIn': {
                  from: { opacity: 0 },
                  to: { opacity: 1 }
                }
              }}
            >
              <Suspense fallback={<ResultsLoader />}>
                <Results 
                  results={currentPlan}
                  onEdit={handleEditResults}
                  isSharedPlan={currentPlan.isSharedPlan || false}
                  hasElevationData={!!currentPlan.formData?.elevationData}
                />
              </Suspense>
            </Paper>
          )}
        </Container>
      </Box>

      {/* Anonymous User Prompt */}
      <AnonymousUserPrompt
        open={showAnonymousPrompt}
        onClose={() => setShowAnonymousPrompt(false)}
      />
    </>
  );
};

export default AppRouter;

